/**
 * Intelligence Panel
 *
 * 2-column widget grid of operational insights.
 * Visually distinct from the order list — no shared dark background.
 */

import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Chip,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StoreIcon from '@mui/icons-material/Store';
import { useHUDTheme } from '../theme';
import type { Insight } from '../context/HUDContext';

/** Driver-shortage is switched off — the signal is not actionable right now. */
const HIDDEN_INSIGHTS = new Set(['driver-shortage']);

const insightIcons: Record<Insight['type'], React.ReactNode> = {
  bottleneck: <WarningAmberIcon sx={{ fontSize: 18 }} />,
  'driver-shortage': <LocalShippingIcon sx={{ fontSize: 18 }} />,
  'store-imbalance': <StoreIcon sx={{ fontSize: 18 }} />,
};

interface InsightCardProps {
  insight: Insight;
  onHighlight: (orderIds: string[]) => void;
  onFilter?: (insightType: string, orderIds: string[]) => void;
}

function InsightCard({ insight, onHighlight, onFilter }: InsightCardProps) {
  const { hud } = useHUDTheme();
  const severityColors: Record<Insight['severity'], string> = {
    critical: hud.accent,
    warning: hud.warning,
    info: hud.info,
  };
  const color = severityColors[insight.severity];

  return (
    <Box
      onClick={() => {
        if (onFilter) {
          onFilter(insight.type, insight.affectedOrderIds);
        } else {
          onHighlight(insight.affectedOrderIds);
        }
      }}
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: 'transparent',
        border: `1px solid ${hud.border}`,
        borderLeft: `3px solid ${color}`,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        '&:hover': {
          bgcolor: `${color}08`,
          borderColor: color,
          boxShadow: `0 2px 8px ${color}20`,
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: `${color}12`,
            flexShrink: 0,
          }}
        >
          {insightIcons[insight.type]}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: hud.textPrimary,
              fontWeight: 600,
              fontSize: '0.78rem',
              lineHeight: 1.3,
            }}
          >
            {insight.title}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: hud.textSecondary,
              fontSize: '0.68rem',
              lineHeight: 1.3,
            }}
          >
            {insight.description}
          </Typography>
        </Box>
        <Chip
          label={insight.count}
          size="small"
          sx={{
            bgcolor: `${color}18`,
            color,
            fontWeight: 700,
            fontSize: '0.75rem',
            height: 22,
            minWidth: 28,
            flexShrink: 0,
          }}
        />
      </Stack>
    </Box>
  );
}

interface IntelligencePanelProps {
  insights: Insight[];
  onHighlightOrders: (orderIds: string[]) => void;
  onInsightFilter?: (insightType: string, orderIds: string[]) => void;
}

export default function IntelligencePanel({
  insights,
  onHighlightOrders,
  onInsightFilter,
}: IntelligencePanelProps) {
  if (insights.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          lg: 'repeat(3, 1fr)',
        },
        gap: 1.5,
        mb: 2,
      }}
    >
      {insights.filter((insight) => !HIDDEN_INSIGHTS.has(insight.type)).map((insight) => (
        <InsightCard
          key={insight.id}
          insight={insight}
          onHighlight={onHighlightOrders}
          onFilter={onInsightFilter}
        />
      ))}
    </Box>
  );
}
